import React, { useRef, useEffect, useMemo } from 'react';
import Chart from 'chart.js/auto';
import { formatCurrency } from '../Utils/chartUtils';
import './Chart.css';
import { useTheme } from '../contexts/ThemeContext';

interface ChartProps {
    labels: string[];
    chartData: number[][];
    additionalDeductionsBreakdown?: { name: string; value: number }[];
}

const COLORS: { [key: string]: string } = {
    income: 'rgba(54, 162, 235, 1)',
    giving: 'rgba(75, 192, 150, 1)',
    deductions: 'rgba(255, 193, 7, 1)',
    cost: 'rgba(214, 105, 120, 1)'
};

const TakehomePayChart: React.FC<ChartProps> = ({ labels, chartData, additionalDeductionsBreakdown = [] }) => {
    const { actualTheme } = useTheme();
    const isDark = actualTheme === 'dark';
    const chartRef = useRef<HTMLCanvasElement>(null);
    const chartInstance = useRef<Chart | null>(null);
    
    const annualIncome = Math.round(chartData[0][0]);
    const monthlyIncome = Math.round(annualIncome / 12);

    // Handle the new data structure with additional deductions
    const charitableGiving = Math.round(chartData[0][1]);
    const additionalDeductions = Math.round(chartData[0][2]);
    
    // Tax values now start from index 3
    const taxTotal = Math.round(chartData[0].slice(3).reduce((a, b) => a + b, 0));
    const taxBreakdown = labels.slice(3).map((label, index) => ({
        name: label,
        value: Math.round(chartData[0][index + 3])
    }));

    const data = useMemo(() => {
        const result = [
            { 
                name: labels[0], 
                value: annualIncome, 
                color: COLORS.income, 
                category: 'income',
                breakdown: [
                    { name: 'Monthly', value: monthlyIncome }
                ]
            },
            { 
                name: labels[1], 
                value: charitableGiving, 
                color: COLORS.giving, 
                category: 'giving',
                breakdown: [
                    { name: 'Monthly', value: Math.round(charitableGiving / 12) }
                ]
            }
        ];

        // Only add additional deductions if there are any
        if (additionalDeductions > 0) {
            result.push({
                name: labels[2], 
                value: additionalDeductions, 
                color: COLORS.deductions, 
                category: 'deductions',
                breakdown: additionalDeductionsBreakdown.length > 0 
                    ? additionalDeductionsBreakdown 
                    : [{ name: 'Monthly', value: Math.round(additionalDeductions / 12) }]
            });
        }

        result.push({
            name: 'Taxes', 
            value: taxTotal, 
            color: COLORS.cost, 
            category: 'cost',
            breakdown: taxBreakdown
        });

        return result;
    }, [labels, annualIncome, monthlyIncome, charitableGiving, additionalDeductions, taxTotal, taxBreakdown, additionalDeductionsBreakdown]);

    useEffect(() => {
        if (!chartRef.current) return;

        const ctx = chartRef.current.getContext('2d');
        if (!ctx) return;

        // Destroy existing chart
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        const theme = isDark ? {
            textColor: 'hsl(0 0% 98%)',
            tooltipBackgroundColor: 'hsl(215 27.9% 16.9%)',
            tooltipTextColor: 'hsl(0 0% 98%)',
            tooltipBorderColor: 'hsl(215 20.2% 65.1%)'
        } : {
            textColor: 'hsl(222.2 84% 4.9%)',
            tooltipBackgroundColor: 'hsl(210 40% 98%)',
            tooltipTextColor: 'hsl(222.2 84% 4.9%)',
            tooltipBorderColor: 'hsl(215 20.2% 65.1%)'
        };

        const customOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true,
                    position: 'bottom' as const,
                    labels: {
                        color: theme.textColor,
                        usePointStyle: true,
                        padding: 20
                    }
                },
                tooltip: {
                    backgroundColor: theme.tooltipBackgroundColor,
                    titleColor: theme.tooltipTextColor,
                    bodyColor: theme.tooltipTextColor,
                    borderColor: theme.tooltipBorderColor,
                    borderWidth: 1,
                    callbacks: {
                        label: function(context: any) {
                            const dataIndex = context.dataIndex;
                            const item = data[dataIndex];
                            const total = data.reduce((sum, d) => sum + d.value, 0);
                            const percentage = ((item.value / total) * 100).toFixed(1);
                            
                            let result = [`${item.name}: ${formatCurrency(item.value)} (${percentage}%)`];
                            
                            if (item.breakdown && item.breakdown.length > 0) {
                                result.push(''); // Empty line
                                item.breakdown.forEach((breakdown: any) => {
                                    result.push(`${breakdown.name}: ${formatCurrency(breakdown.value)}`);
                                });
                            }
                            
                            return result;
                        }
                    }
                }
            }
        };

        chartInstance.current = new Chart(ctx, {
            type: 'doughnut',
            data: {
                labels: data.map(d => d.name),
                datasets: [{
                    data: data.map(d => d.value),
                    backgroundColor: data.map(d => d.color),
                    borderWidth: 2,
                    borderColor: isDark ? 'hsl(215 27.9% 16.9%)' : 'hsl(210 40% 98%)'
                }]
            },
            options: customOptions
        }) as Chart;
    }, [chartData, labels, isDark, data]);

    return (
        <div className="chart-container">
            <canvas ref={chartRef}></canvas>
        </div>
    );
};

export default TakehomePayChart;
